import {
  Badge,
  Button,
  Center,
  chakra,
  Flex,
  HStack,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ErrorBox from "../../../shared/components/ErrorBox";
import useApi from "../../../shared/hooks/useApi";
import { components, PatientId, TaskDocumentRejectionId } from "../../../shared/schema/schema";
import { dateFormatter } from "../../../shared/utils/date-formatter";

type PatientTaskDocumentRejection = components["schemas"]["PatientTaskDocumentRejection"];
type RejectionType = components["schemas"]["RejectionType"];

function RejectionTypeBadge({ type }: { type: RejectionType }) {
  switch (type) {
    case "blocked":
      return (
        <Badge colorScheme="red" display="inline-flex">
          Blocked
        </Badge>
      );
    case "allowed":
      return (
        <Badge colorScheme="green" display="inline-flex">
          Allowed
        </Badge>
      );
    case "allowed_with_flags":
      return (
        <Badge colorScheme="yellow" display="inline-flex">
          Allowed with Flags
        </Badge>
      );
  }
}

function RejectionStatusBadge({ rejection }: { rejection: PatientTaskDocumentRejection }) {
  if (rejection.resolvedAt) {
    return (
      <Badge colorScheme="green" display="inline-flex">
        Resolved
      </Badge>
    );
  }
  return (
    <Badge colorScheme="yellow" display="inline-flex">
      Pending
    </Badge>
  );
}

export default function PatientTaskDocumentRejectionsTable(props: {
  patientId: PatientId;
  onUpdate?: () => void;
}) {
  const { api, queries } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();

  const queryOptions = queries.patient.taskDocumentRejections(props.patientId);
  const query = useQuery(queryOptions);

  const resolveMutation = useMutation({
    mutationFn: async (rejectionId: TaskDocumentRejectionId) => {
      await api.post("./task_document_rejections/:rejectionId/resolve", {
        path: { rejectionId },
      });
    },
    onSuccess: () => {
      toast({
        position: "top-right",
        title: "Rejection resolved",
        status: "success",
      });
      queryClient.invalidateQueries({
        queryKey: queryOptions.queryKey,
      });
      props.onUpdate?.();
    },
    onError: (error: Error) => {
      toast({
        position: "top-right",
        title: "Failed to resolve rejection",
        description: error.message,
        status: "error",
      });
    },
  });

  return (
    <Flex direction="column" gap="5">
      <Text fontSize="lg" fontWeight="semibold">
        Task Document Rejections
      </Text>
      <TableContainer>
        <Table w="100%">
          <Thead>
            <Tr>
              <Th>Document</Th>
              <Th>Rejection</Th>
              <Th>Flags</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Created By</Th>
              <Th>Created At</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {(() => {
              switch (query.status) {
                case "pending":
                  return (
                    <Tr>
                      <Td colSpan={8}>
                        <Center>
                          <Spinner size="sm" />
                        </Center>
                      </Td>
                    </Tr>
                  );
                case "error":
                  return (
                    <Tr>
                      <Td colSpan={8}>
                        <ErrorBox error={query.error} resetErrorBoundary={query.refetch} />
                      </Td>
                    </Tr>
                  );
                case "success":
                  return query.data.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <NoResults>No rejections</NoResults>
                      </Td>
                    </Tr>
                  ) : (
                    query.data.map((rejection) => (
                      <Tr key={rejection.id}>
                        <Td>{rejection.documentTitle}</Td>
                        <Td sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                          {rejection.rejection}
                        </Td>
                        <Td>
                          {rejection.rejectionFlags.length > 0 ? (
                            <HStack flexWrap="wrap" spacing={1}>
                              {rejection.rejectionFlags.map((flag, idx) => (
                                <Badge key={idx} colorScheme="orange" size="sm" variant="subtle">
                                  {flag}
                                </Badge>
                              ))}
                            </HStack>
                          ) : (
                            <Text color="gray.400">-</Text>
                          )}
                        </Td>
                        <Td>
                          <RejectionTypeBadge type={rejection.rejectionType} />
                        </Td>
                        <Td>
                          <RejectionStatusBadge rejection={rejection} />
                        </Td>
                        <Td>{rejection.createdBy}</Td>
                        <Td>{dateFormatter.toDateTime(rejection.createdAt)}</Td>
                        <Td>
                          {rejection.rejectionType === "blocked" && !rejection.resolvedAt && (
                            <Button
                              colorScheme="blue"
                              isLoading={resolveMutation.isPending}
                              size="sm"
                              onClick={() => resolveMutation.mutate(rejection.id)}
                            >
                              Resolve
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    ))
                  );
              }
            })()}
          </Tbody>
        </Table>
      </TableContainer>
    </Flex>
  );
}

const NoResults = chakra("div", {
  base: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "full",
    bg: "bg.subtle",
    color: "fg.muted",
    rounded: "l2",
    py: "4",
  },
});
